/**
 * Screen-local fetch module for the Test Runs screen (epic 031 task 06).
 *
 * ADR-010 A2 (b): `lib/api.ts` is never edited by a Stage-C epic — each new
 * screen talks to the api through a module like this one. Same base-URL
 * expression, the same `{ data }` envelope unwrapping and the same `ApiError`
 * (imported) as `lib/api.ts`, so screens keep one error type and screen tests
 * mock this module, not the fetch.
 *
 * Response contracts come from `@leads-platform/shared` (task 01's
 * `test-run.admin.schema.ts`, ADR-010 A3) — no local type mirror. The create
 * body is the one exception: shared exports the *output* type
 * (`CreateTestRunRequest`, post-coercion — `campaignLinkId: bigint`), while
 * this module builds the wire payload, so it declares the **input** shape
 * (conventions §Type-safety: build against `z.input`; bigint FKs ride the
 * wire as strings). `zod` is not a dependency of this package, so the input
 * type is restated here against task 01's schema — the runtime contract is
 * still shared's, asserted by the api's ZodValidationPipe.
 */

import type {
  ActivationCheck,
  ApiResponse,
  CampaignResponse,
  TestRunDetail,
  TestRunList,
  TestRunMode,
  TestRunSummary,
} from '@leads-platform/shared';
import {
  api,
  apiFetch,
  ApiError,
  type CampaignDetailResponse,
  type CampaignLinkRow,
} from '../../lib/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * The create body as the SPA holds it — the input shape of shared's
 * `createTestRunRequestSchema`: `campaignLinkId` as the string a `<select>`
 * carries (coerced to bigint api-side), `testEmail` optional in admin mode
 * (the `qa+<runId>@tradingtips.com` convention fills it server-side).
 */
export interface CreateTestRunBody {
  mode: TestRunMode;
  testEmail?: string;
  campaignLinkId?: string;
  siteId?: string;
  subId?: string;
  requestedBy?: string;
}

/**
 * `ApiError` widened with the api's machine-readable error `code`
 * (`test_run_not_applicable` on 400, `test_run_in_progress` /
 * `test_lead_exists` on 409). Still an `ApiError` — the screen's
 * `.catch((err: ApiError) => …)` idiom keeps working unchanged.
 */
export class TestRunApiError extends ApiError {
  constructor(
    status: number,
    message: string,
    public readonly code?: string
  ) {
    super(status, message);
    this.name = 'TestRunApiError';
  }
}

/**
 * `message` of a Nest error body without a cast: narrow the unknown JSON with
 * the `in` operator, then the string check. Falls back to the HTTP status text.
 */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = body.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/** The api's machine-readable `code` on service-thrown 4xx bodies, if any. */
function codeFromBody(body: unknown): string | undefined {
  if (typeof body === 'object' && body !== null && 'code' in body) {
    const code = body.code;
    if (typeof code === 'string' && code.length > 0) return code;
  }
  return undefined;
}

/**
 * One request over the admin api: unwrap `{ data }`, map every non-2xx to
 * `TestRunApiError` (an `ApiError` carrying the body's `code`).
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...init?.headers },
    });
  } catch {
    throw new ApiError(0, 'Network error');
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    throw new TestRunApiError(
      response.status,
      messageFromBody(errorData, response.statusText),
      codeFromBody(errorData)
    );
  }

  const envelope: ApiResponse<T> = JSON.parse(await response.text());
  return envelope.data;
}

/** Campaigns for the picker — reuses the shared typed client (read-only import). */
export function getCampaigns(): Promise<CampaignDetailResponse[]> {
  return api.getCampaigns();
}

/** CTO links of one campaign — reuses the shared typed client (read-only import). */
export function getLinks(campaignId: string): Promise<CampaignLinkRow[]> {
  return api.getCampaignLinks(campaignId);
}

/** GET /admin/campaigns/:id/test-runs — the 50 newest runs, newest first. */
export function listRuns(campaignId: string): Promise<TestRunList> {
  return request<TestRunList>(`/admin/campaigns/${campaignId}/test-runs`);
}

/** GET /admin/campaigns/:id/test-runs/:runId — one run with its lead and attempts. */
export function getRun(campaignId: string, runId: string): Promise<TestRunDetail> {
  return request<TestRunDetail>(`/admin/campaigns/${campaignId}/test-runs/${runId}`);
}

/** POST /admin/campaigns/:id/test-runs — create an admin-mode or cto_link run. */
export function createRun(campaignId: string, body: CreateTestRunBody): Promise<TestRunSummary> {
  return request<TestRunSummary>(`/admin/campaigns/${campaignId}/test-runs`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** GET /admin/campaigns/:id/test-runs/activation-check — the FR-A25 gate state. */
export function activationCheck(campaignId: string): Promise<ActivationCheck> {
  return request<ActivationCheck>(`/admin/campaigns/${campaignId}/test-runs/activation-check`);
}

/** POST /admin/campaigns/:id/test-mode — the test-mode lifecycle toggle (task 05). */
export function setTestMode(campaignId: string, enabled: boolean): Promise<CampaignResponse> {
  return request<CampaignResponse>(`/admin/campaigns/${campaignId}/test-mode`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}
